import { bucketOfTask, type BucketableTask } from '../agent-task-buckets';
import { isScheduledTask, notBeforeMs } from '../task-schedule';
import { fromDayParam } from './calendar-dates';
import type { BookingClient, CalendarItem, CalendarPerson, GoalTaskRow, GoalTasks } from './calendar-item';

// What the calendar draws, from each source's answer. The connected calendars and Bookings come from
// the harness already drawn (`/api/calendar/*`); what is left here is the part only the reader's own
// device can do (which local day an all-day item is on, the provider's page for that day) and the
// Symphony layers, whose rows every client already reads.

const HOUR = 3_600_000;

/** A meeting or a booking, as `/api/calendar/meetings` and `/api/calendar/bookings` send it. */
export interface WireCalendarItem {
  key: string;
  kind: 'meeting' | 'booking';
  title: string;
  /** ISO instants. */
  start: string;
  end: string;
  /** An all-day item's first day and the day after its last, as calendar dates. */
  allDay?: { startDate: string; endDate: string };
  sourceId: string;
  sourceLabel?: string;
  calendar?: { id: string; name: string; primary?: boolean };
  source?: { provider?: string; logoMediaId?: string; logoUrl?: string };
  color?: string;
  rsvp?: CalendarItem['rsvp'];
  booking?: { clients: BookingClient[] };
  needsYou?: boolean;
  settled?: boolean;
  status?: string;
  location?: string;
  people?: CalendarPerson[];
  hasGuests?: boolean;
  staff?: string[];
  staffResourceIds?: string[];
  capacity?: { booked: number; total: number };
  videoLink?: string;
  movable?: boolean;
  href?: string;
  external?: boolean;
  bookingUrl?: string;
  price?: { amount: string; currency: string };
  busy?: boolean;
  ref: Record<string, string>;
}

/** The provider's own calendar, opened on the reader's local day. */
function providerDayLink(provider: string | undefined, at: number): string | undefined {
  const day = new Date(at);
  if (provider === 'GOOGLE') {
    return `https://calendar.google.com/calendar/r/day/${day.getFullYear()}/${day.getMonth() + 1}/${day.getDate()}`;
  }
  if (provider === 'MICROSOFT') return 'https://outlook.live.com/calendar/';
  return undefined;
}

const MEDIA_ID = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,255}$/;

/** Accepts the current identifier and the old trusted Wix URL, never another host. */
export function wixMediaId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (MEDIA_ID.test(value)) return value;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'static.wixstatic.com' || !url.pathname.startsWith('/media/')) return undefined;
    const id = decodeURIComponent(url.pathname.slice('/media/'.length));
    return !id.includes('/') && MEDIA_ID.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}

function publicHttpsUrl(value: string | undefined): string | undefined {
  if (!value || value.length > 2048 || value !== value.trim()) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password ? value : undefined;
  } catch {
    return undefined;
  }
}

const AMOUNT = /^\d{1,9}(\.\d{1,4})?$/;
const CURRENCY = /^[A-Z]{3}$/;

function fixedPrice(price: WireCalendarItem['price']): price is { amount: string; currency: string } {
  return !!price && typeof price.amount === 'string' && AMOUNT.test(price.amount) && typeof price.currency === 'string' && CURRENCY.test(price.currency);
}

/** A wire item on the business clock. One that can't be placed is left out. */
export function fromWireItem(wire: WireCalendarItem, timeZone?: string): CalendarItem | undefined {
  const start = wire.allDay ? fromDayParam(wire.allDay.startDate, timeZone) : Date.parse(wire.start);
  const end = wire.allDay ? fromDayParam(wire.allDay.endDate, timeZone) : Date.parse(wire.end);
  if (typeof start !== 'number' || !Number.isFinite(start)) return undefined;
  const normalizedEnd = typeof end === 'number' && Number.isFinite(end) && end > start ? end : start + HOUR;
  const { allDay: _dates, people, booking, hasGuests, source, movable, href: _href, external: _external, bookingUrl: _bookingUrl, price, busy, ...rest } = wire;
  const href =
    wire.kind === 'booking' ? undefined : wire.href ?? (wire.kind === 'meeting' ? providerDayLink(wire.source?.provider, start) : undefined);
  const logoMediaId = wixMediaId(source?.logoMediaId ?? source?.logoUrl);
  const safeSource =
    source && (source.provider || logoMediaId)
      ? { ...(source.provider ? { provider: source.provider } : {}), ...(logoMediaId ? { logoMediaId } : {}) }
      : undefined;
  const clientNames = people?.flatMap((person) => (person.name ? [{ name: person.name }] : []));
  const safeBooking = booking
    ? {
        clients: booking.clients.map(({ id, revision, name, status, payment, paymentOption, attendance, bookedBy }) => ({
          ...(id ? { id } : {}),
          ...(revision ? { revision } : {}),
          ...(name ? { name } : {}),
          ...(status ? { status } : {}),
          ...(payment ? { payment } : {}),
          ...(paymentOption ? { paymentOption } : {}),
          ...(attendance ? { attendance } : {}),
          ...(bookedBy ? { bookedBy } : {}),
        })),
      }
    : undefined;
  return {
    ...rest,
    start,
    end: normalizedEnd,
    allDay: !!wire.allDay,
    ...(wire.allDay ? { allDayStartDate: wire.allDay.startDate, allDayEndDate: wire.allDay.endDate } : {}),
    ...(safeSource ? { source: safeSource } : {}),
    ...(wire.kind === 'meeting' && (hasGuests || (people?.length ?? 0) > 0) ? { hasGuests: true } : {}),
    ...(wire.kind === 'meeting' && movable === true ? { movable: true } : {}),
    ...(wire.kind === 'meeting' && busy === true ? { busy: true } : {}),
    ...(wire.kind === 'booking' && clientNames?.length ? { people: clientNames } : {}),
    ...(wire.kind === 'booking' && safeBooking ? { booking: safeBooking } : {}),
    ...(wire.kind === 'booking' && publicHttpsUrl(wire.bookingUrl) ? { bookingUrl: wire.bookingUrl } : {}),
    ...(wire.kind === 'booking' && fixedPrice(price) ? { price: { amount: price.amount, currency: price.currency } } : {}),
    ...(href ? { href, external: true } : {}),
  };
}

/** The fields of a task the calendar reads. `WebTask` satisfies it. */
export type CalendarTask = BucketableTask & {
  id: string;
  title?: string;
  workspaceId?: string;
  updatedDate?: string | Date;
  agentId?: string;
  goalId?: string;
};

/**
 * Pending work with a real schedule sits at `notBefore`. Completed work stays out until the task
 * lifecycle exposes an authoritative completion instant; `updatedDate` is mutable and cannot prove it.
 */
export function taskItems(tasks: CalendarTask[], now: number = Date.now()): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const task of tasks) {
    if (!task?.id) continue;
    const bucket = bucketOfTask(task);
    const settled = bucket === 'done' || bucket === 'failed';
    if (settled) continue;
    const at = notBeforeMs(task);
    if (at == null || !Number.isFinite(at)) continue;
    items.push({
      key: `task:${task.id}`,
      kind: 'task',
      title: task.title || '',
      start: at,
      end: at,
      allDay: false,
      needsYou: bucket === 'waiting',
      settled,
      status: bucket === 'waiting' ? bucket : isScheduledTask(task, now) ? 'scheduled' : bucket,
      workspaceId: task.workspaceId || undefined,
      href: `/tasks/${encodeURIComponent(task.id)}`,
      ref: { taskId: task.id, agentId: task.agentId, goalId: task.goalId },
    });
  }
  return items;
}

/** The fields of a routine the calendar reads: a `WebRoutine` read with a window, which lists its runs. */
export interface CalendarRoutine {
  id: string;
  name?: string;
  workspaceId?: string;
  cron?: string;
  /** Its fire times in the window asked for, as ISO instants, by the runtime's own day rule. */
  runs?: string[];
  runsEveryDay?: boolean;
}

/** Every run of each routine inside the window it was read for. A daily one is marked so the month grid leaves it out. */
export function routineItems(routines: CalendarRoutine[]): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const routine of routines) {
    if (!routine?.id) continue;
    for (const run of routine.runs ?? []) {
      const at = Date.parse(run);
      if (!Number.isFinite(at)) continue;
      items.push({
        key: `routine:${routine.id}:${at}`,
        kind: 'routine',
        title: routine.name || '',
        start: at,
        end: at,
        allDay: false,
        repeatsDaily: routine.runsEveryDay === true,
        workspaceId: routine.workspaceId || undefined,
        ref: { routineId: routine.id, cron: routine.cron },
      });
    }
  }
  return items;
}

export interface CalendarGoal {
  id: string;
  title?: string;
  status?: string;
  outcome?: string;
  workspaceId?: string;
  endsAt?: string;
}

/**
 * A goal's own tasks for its deadline: each with one status and its scheduled date, or none. Dated tasks
 * come first in date order, then the undated ones; a task is listed once however many reads carried it.
 * Only tasks group under a goal; a routine stays separate even when it serves one.
 */
export function goalTasks(goalId: string, tasks: CalendarTask[], now: number = Date.now()): GoalTasks {
  const seen = new Set<string>();
  const rows: GoalTaskRow[] = [];
  for (const task of tasks) {
    if (!task?.id || task.goalId !== goalId || seen.has(task.id)) continue;
    seen.add(task.id);
    const bucket = bucketOfTask(task);
    const status = bucket === 'inProgress' && isScheduledTask(task, now) ? 'scheduled' : bucket;
    const at = notBeforeMs(task);
    rows.push({ taskId: task.id, title: task.title || '', status, ...(at != null && Number.isFinite(at) ? { at } : {}) });
  }
  rows.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity) || a.title.localeCompare(b.title) || a.taskId.localeCompare(b.taskId));
  return { done: rows.filter((row) => row.status === 'done').length, total: rows.length, tasks: rows };
}

/** Open goals with a real end date appear at that recorded deadline; settled and undated goals do not. */
export function goalItems(goals: CalendarGoal[], tasks: CalendarTask[] = [], now: number = Date.now()): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const goal of goals) {
    if (!goal?.id || goal.outcome || goal.status === 'ACHIEVED' || goal.status === 'ABANDONED' || goal.status === 'CANCELLED') continue;
    const at = Date.parse(goal.endsAt ?? '');
    if (!Number.isFinite(at)) continue;
    items.push({
      key: `goal:${goal.id}`,
      kind: 'goal',
      title: goal.title || '',
      start: at,
      end: at,
      allDay: false,
      workspaceId: goal.workspaceId || undefined,
      href: `/goals?goal=${encodeURIComponent(goal.id)}`,
      goalTasks: goalTasks(goal.id, tasks, now),
      ref: { goalId: goal.id },
    });
  }
  return items;
}
